import { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MARQUEE_ITEMS } from "../../content";

function MaskedLine({ children, delay, reduce }) {
  return (
    <span className="block overflow-hidden pb-[0.08em]">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export function Hero() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 90]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-16 sm:pt-[4.5rem]">
      <div className="mx-auto grid max-w-7xl gap-8 px-5 pb-10 pt-10 sm:px-8 sm:pb-14 sm:pt-14 lg:grid-cols-[1.12fr_0.88fr] lg:items-center lg:gap-8 lg:pt-20">
        <div>
          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.28em] text-cherry"
          >
            <span className="h-px w-8 bg-gold" aria-hidden="true" />
            Independent AML &amp; Regulatory Compliance Consultancy
          </motion.p>

          <h1 className="mt-5 font-display text-[2.65rem] leading-[1.04] tracking-tight text-ink sm:mt-7 sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.25} reduce={reduce}>AML compliance,</MaskedLine>
            <MaskedLine delay={0.4} reduce={reduce}>
              <em className="text-cherry">made clear.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-ink/70 sm:mt-6 sm:text-lg"
          >
            Independent AML, regulatory compliance and commercial contracts support for businesses. I help
            organisations review their existing arrangements, address compliance gaps and manage their day-to-day
            legal and regulatory requirements.
          </motion.p>

          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-4 text-sm font-medium text-ink/60"
            data-testid="hero-credentials"
          >
            11 years&rsquo; experience&nbsp;|&nbsp;ICA-qualified&nbsp;|&nbsp;UK, UAE &amp; Australia
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-7 flex flex-col gap-3 sm:mt-8 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4"
          >
            <a
              href="#services"
              data-testid="hero-cta-services"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-cherry px-7 py-3 text-sm font-medium text-ivory transition-colors duration-200 hover:bg-cherry-deep"
            >
              Explore Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/25 px-7 py-3 text-sm font-medium text-ink transition-colors duration-200 hover:border-cherry hover:text-cherry"
            >
              Get in Touch
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative hidden lg:block"
        >
          <motion.p
            aria-hidden="true"
            style={{ y: markY }}
            className="pointer-events-none absolute -left-10 -top-14 z-[2] select-none font-display text-[11rem] italic leading-none text-cherry/10"
          >
            A·M
          </motion.p>
          <div className="absolute -bottom-5 -right-5 h-full w-full rounded-sm border border-gold" aria-hidden="true" />
          <div className="relative overflow-hidden rounded-sm">
            <motion.img
              src="/assets/london-hero.jpg"
              alt="Modern architecture in a financial district"
              style={{ y: imgY, scale: 1.12 }}
              className="h-[540px] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cherry-deep/45 via-transparent to-transparent" aria-hidden="true" />
            <p className="absolute bottom-5 left-5 max-w-[240px] text-xs leading-relaxed text-ivory/90">
              Practical AML support, built around how your business works.
            </p>
          </div>
        </motion.div>
      </div>

      <div className="border-y border-ink/10 bg-warmwhite py-3.5" aria-hidden="true">
        <div className="overflow-hidden">
          <div className="marquee-track flex w-max items-center gap-10 whitespace-nowrap">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <span key={i} className="text-[11px] font-medium uppercase tracking-[0.3em] text-ink/50">
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
